import { forwardRef } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { HelpIcon } from "@/components/shared/HelpIcon";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";

interface FormDatePickerProps {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  value?: string | Date | null;
  onChange?: (date: Date | null) => void;
  onBlur?: () => void;
  className?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  tooltip?: boolean;
  tooltipMessage?: React.ReactNode;

  minDate?: Date;
  maxDate?: Date;
}

export const FormDatePicker = forwardRef<HTMLButtonElement, FormDatePickerProps>(
  (
    {
      label,
      error,
      required,
      helperText,
      value,
      onChange,
      onBlur,
      className,
      id,
      placeholder,
      disabled,
      tooltip,
      tooltipMessage,
      minDate,
      maxDate,
    },
    ref,
  ) => {
    // --- FUNÇÃO DE CORREÇÃO ---
    const parseDate = (val: string | Date | null | undefined): Date | undefined => {
      if (!val) return undefined;

      // Se for uma string simples YYYY-MM-DD (length 10), forçamos o horário local
      // adicionando "T00:00:00" ao final. Isso evita a conversão automática para UTC.
      if (typeof val === "string" && val.length === 10 && val.includes("-")) {
        return new Date(`${val}T00:00:00`);
      }

      return new Date(val);
    };
    // --------------------------

    const dateObject = parseDate(value);

    const handleDateSelect = (date: Date | null) => {
      onChange?.(date || null);
    };

    const handlePopoverOpenChange = (open: boolean) => {
      if (!open) {
        onBlur?.();
      }
    };

    return (
      <div className="space-y-2">
        {label && (
          <div className="flex h-[20px] items-center gap-2">
            <Label
              htmlFor={id}
              className={cn("text-sm font-medium text-foreground", error && "text-destructive")}
            >
              {required && <span className="mr-1 text-destructive">*</span>}
              {label}
            </Label>
            {tooltip && <HelpIcon tooltipMessage={tooltipMessage} />}
          </div>
        )}
        {/* 1. Adicionamos modal={true} AQUI */}
        <Popover modal={true} onOpenChange={handlePopoverOpenChange}>
          <PopoverTrigger className="min-h-10 w-full">
            <Button
              ref={ref}
              type="button" // 2. Adicionamos type="button" AQUI
              variant="outline"
              className={cn(
                "min-h-10 w-full justify-start bg-transparent text-left font-normal dark:bg-input/30",
                !value && "text-muted-foreground",
                "disabled:pointer-events-auto disabled:cursor-not-allowed disabled:text-muted-foreground",
                error &&
                  "border-destructive text-destructive focus:border-destructive focus:ring-destructive",
                className,
              )}
              disabled={disabled}
            >
              <CalendarIcon
                className={cn("mr-2 h-3 w-3", error ? "text-destructive" : "text-muted-foreground")}
              />
              {dateObject ? (
                <span className={cn(error && "text-foreground")}>
                  {format(dateObject, "dd/MM/yyyy")}
                </span>
              ) : (
                <span className={cn(error ? "text-destructive/70" : "")}>
                  {placeholder || "Selecione uma data"}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="pointer-events-auto w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateObject}
              onSelect={handleDateSelect}
              disabled={(date) => {
                return (maxDate && date > maxDate) || (minDate && date < minDate);
              }}
            />
          </PopoverContent>
        </Popover>

        <ErrorTextComponent error={error} />
        <HelperTextComponent text={helperText} error={error} />
      </div>
    );
  },
);

FormDatePicker.displayName = "FormDatePicker";
